import { displayDiagnosis } from '../agent-common/diagnosisLabels';
import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import TierChip from './TierChip';
import { driverLabel } from './WhyCard';
import { correctionAnnotation, driverKey, type CorrectionInput } from './corrections';
import { createAnnotation, type Annotation, type DiagnoseDriver, type DiagnoseEnvelope } from '../../apis';
import { stromaInset } from '../LandingAgent/askPresentation';

const OTHER = '__other__';

interface Props {
  envelope: DiagnoseEnvelope;
  perspective?: string | null;
  sessionId?: string | null;
  onSaved: (saved: Annotation & Record<string, any>) => void;
  onCancel: () => void;
}

/**
 * "This is wrong — correct it" (#186): pick the true driver from the walk's
 * candidates or name it, add why and an evidence link, save. What is saved
 * is an annotation of kind `correction`, bound to this metric, window and
 * perspective, stamped with who by the server — a layer, never an edit.
 */
export default function VerdictEditor({ envelope, perspective, sessionId, onSaved, onCancel }: Props) {
  const candidates: DiagnoseDriver[] = (envelope.drivers || []).filter((d) => d.kind !== 'text');
  const [choice, setChoice] = useState<string>(candidates[0] ? driverKey(candidates[0]) : OTHER);
  const [other, setOther] = useState('');
  const [note, setNote] = useState('');
  const [evidence, setEvidence] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = candidates.find((d) => driverKey(d) === choice) || null;
  const input: CorrectionInput | null = chosen
    ? { driver: String(chosen.id), driver_kind: chosen.kind as CorrectionInput['driver_kind'], driver_label: driverLabel(chosen), driver_metric: chosen.kind === 'segment' ? chosen.metric || null : null, evidence_url: evidence, note }
    : other.trim() ? { driver: other.trim(), driver_kind: 'text', driver_label: other.trim(), evidence_url: evidence, note } : null;

  const save = async () => {
    if (!input || saving || !perspective) return;
    setSaving(true); setError(null);
    try {
      const saved = await createAnnotation(correctionAnnotation(envelope, input, { perspective, sessionId }));
      onSaved(saved as any);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Could not save the correction');
      setSaving(false);
    }
  };

  return (
    <Box data-testid="verdict-editor" sx={{ p: 1.5, ...stromaInset, borderColor: 'divider', display: 'grid', gap: 1.25 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>What actually drove this?</Typography>
      <FormControl size="small" fullWidth>
        <InputLabel id="verdict-driver-label">True driver</InputLabel>
        <Select labelId="verdict-driver-label" label="True driver" value={choice} onChange={(e) => setChoice(String(e.target.value))}
                inputProps={{ 'aria-label': 'True driver', 'data-testid': 'verdict-driver' }}>
          {candidates.map((d) => (
            <MenuItem key={driverKey(d)} value={driverKey(d)}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>{driverLabel(displayDiagnosis({ drivers: [d] }).drivers[0])} <TierChip tier={d.tier} /></Box>
            </MenuItem>
          ))}
          <MenuItem value={OTHER}>Something else…</MenuItem>
        </Select>
      </FormControl>
      {choice === OTHER && (
        <TextField size="small" fullWidth label="Name the driver" value={other} onChange={(e) => setOther(e.target.value)}
                   inputProps={{ 'aria-label': 'Name the driver' }} />
      )}
      <TextField size="small" fullWidth multiline minRows={1} maxRows={4} label="Why (optional)" value={note}
                 onChange={(e) => setNote(e.target.value)} inputProps={{ 'aria-label': 'Why' }} />
      <TextField size="small" fullWidth label="Evidence link (optional)" value={evidence} onChange={(e) => setEvidence(e.target.value)}
                 inputProps={{ 'aria-label': 'Evidence link' }} placeholder="https://…" />
      {error && <Typography variant="caption" color="error">{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button size="small" variant="contained" onClick={save} disabled={!input || saving}>Save correction</Button>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        Saved with your name and the time. The walk's own verdict stays in the trace; a correction is never deleted.
      </Typography>
    </Box>
  );
}
